/**
 * GameSession — the core loop, in one place.
 *
 * GDD §36 sets the GOD-0 gate as: "a developer should not have to interpret the
 * core loop." This file is that answer:
 *
 *     ROLL -> MOVE -> ENCOUNTER -> CHOICE -> CONSEQUENCE -> MEMORY -> CHRONICLE
 *
 * Everything else in the engine is a subsystem this orchestrates. All state
 * changes route through `emit`, so the event log stays authoritative.
 */

import {
  bandOf,
  createDiceState,
  double as mDouble,
  gamble as mGamble,
  hold as mHold,
  reroll as mReroll,
  freeReroll as mFreeReroll,
  sacrifice as mSacrifice,
  bank as mBank,
  split as mSplit,
  relieveFateDebt,
  addFateDebt,
  rememberWithDie,
  rollDie,
  spendBanked,
  updateStreaks,
  BAD_LUCK_TRIGGER,
  FATE_CHAIN_TRIGGER,
  type DiceFace,
  type DieDefinition,
  type ManipulationKind,
  type ManipulationResult,
  type RollRecord,
} from './dice.js';
import { Dungeon, type RoomDef } from './board.js';
import {
  MAX_HAND_SIZE,
  STARTING_HAND_SIZE,
  baseCardId,
  isPlayable,
  resolveVariant,
  type CardContext,
  type CardDefRef,
} from './cards.js';
import {
  createCombat,
  rollLoot,
  stepCombat,
  type CombatState,
  type CombatTactic,
  type MonsterDef,
} from './combat.js';
import {
  BAD_LUCK_CONTENT,
  FATE_CHAIN_CONTENT,
  FATE_EVENT_CONTENT,
  compileEffects,
  createEncounterRuntime,
  outcomeFor,
  streakContentFor,
  visibleChoices,
  wait as encounterWait,
  type ChoiceAvailability,
  type EncounterDef,
  type EncounterRuntime,
  type StreakContent,
} from './encounter.js';
import { EventLog, type EventDraft, type GameEvent } from './events.js';
import {
  evaluateCondition,
  fragmentKey,
  relationshipStage,
  duePromises,
  selectFragments,
  type DialogueContext,
  type DialogueFragment,
  type NpcMemory,
  type SelectedFragment,
} from './npc.js';
import { RngBundle, presentationSeed, type RngStream } from './rng.js';
import {
  applyEvent,
  checkHiddenClasses,
  createGameState,
  npcMemory,
  restOutcome,
  restedFlag,
  statsAtLevel,
  xpForNextLevel,
  HP_PER_LEVEL,
  type GameState,
} from './state.js';
import { buildChronicle, checkTitles, summarizeRun, type ChronicleChapter, type RunSummary } from './chronicle.js';
import { MemoryAnalyticsSink, type AnalyticsSink } from './analytics.js';
import { deserializeSave, serializeSave, type ContentResolver, type SaveFile } from './save.js';
import type { ClassId, NpcId, RoomId } from './types.js';

/**
 * Authored NPC knowledge. Seeded into NpcMemory on first contact so that the
 * `knows:` / `conceals:` conditions have something to read, without every save
 * having to carry a copy of static content.
 */
export interface NpcSeed {
  readonly knownFacts: readonly string[];
  readonly concealedFacts: readonly string[];
}

/** Everything the session needs from the content package. */
export interface ContentBundle {
  readonly dungeon: Dungeon;
  readonly monsters: ReadonlyMap<string, MonsterDef>;
  readonly encounters: ReadonlyMap<string, EncounterDef>;
  readonly dialogue: ReadonlyMap<string, readonly DialogueFragment[]>;
  readonly npcNames: ReadonlyMap<string, string>;
  readonly npcSeeds?: ReadonlyMap<string, NpcSeed>;
  /** Cards by id. Without this the deck is a list of strings nobody can play. */
  readonly cards?: ReadonlyMap<string, CardDefRef>;
  readonly dice: readonly DieDefinition[];
  readonly startingDeck: readonly string[];
  readonly startingItems: Partial<Record<ClassId, readonly string[]>>;
  readonly easterEggs: ReadonlyMap<string, EasterEggDef>;
}

export interface EasterEggDef {
  readonly id: string;
  readonly name: string;
  readonly hint: string;
  /** Condition in the npc.ts grammar, evaluated against world state. */
  readonly when: string;
  readonly reveal: string;
}

export type Phase =
  | 'classSelect'
  | 'awaitRoll'
  | 'awaitMove'
  | 'encounter'
  | 'combat'
  | 'dialogue'
  | 'runOver';

/** Phases in which the player may roll. */
const ROLLABLE_PHASES: ReadonlySet<Phase> = new Set<Phase>([
  'awaitRoll',
  'encounter',
  'dialogue',
  'combat',
]);

export interface SessionEvent {
  readonly kind:
    | 'rolled'
    | 'manipulated'
    | 'moved'
    | 'encounterOpened'
    | 'choiceResolved'
    | 'combatRound'
    | 'combatEnded'
    | 'cardDrawn'
    | 'cardPlayed'
    | 'streak'
    | 'fateEvent'
    | 'easterEgg'
    | 'titleEarned'
    | 'hiddenClass'
    | 'promiseBroken'
    | 'rested'
    | 'stayed'
    | 'runEnded';
  readonly text?: string;
  readonly data?: Record<string, unknown>;
  /** Audio id the client should play. Never a file path (CLAUDE.md §4.4). */
  readonly sfxId?: string;
}

export interface SessionOptions {
  readonly runSeed: string;
  readonly classId: ClassId;
  readonly playerName?: string;
  readonly content: ContentBundle;
  readonly analytics?: AnalyticsSink;
  /** Real-world clock, injected so tests are deterministic. */
  readonly now?: () => number;
}

export class GameSession {
  readonly state: GameState;
  readonly log: EventLog;
  readonly content: ContentBundle;
  readonly analytics: AnalyticsSink;

  private readonly rng: RngBundle;
  private readonly now: () => number;
  private readonly createdAtMs: number;

  phase: Phase = 'awaitRoll';
  /** The roll waiting to be spent, if any. */
  pendingRoll: RollRecord | null = null;
  /** Movement points available from the pending roll. */
  movementPoints = 0;
  encounter: EncounterRuntime | null = null;
  combat: CombatState | null = null;
  activeMonster: MonsterDef | null = null;
  activeNpcId: NpcId | null = null;
  activeDieId: string;
  /** Emitted since the last `drain()`. The client renders these. */
  private outbox: SessionEvent[] = [];
  private usedFragments = new Set<string>();
  /** 인카운터별 대사 선택 결과. 렌더가 `once` 대사를 소비하지 않게 막는다. */
  private dialogueCache = new Map<string, SelectedFragment[]>();
  /** Free rerolls granted by Fate Chain. */
  freeRerolls = 0;

  constructor(opts: SessionOptions) {
    this.content = opts.content;
    this.analytics = opts.analytics ?? new MemoryAnalyticsSink();
    this.now = opts.now ?? (() => Date.now());
    this.createdAtMs = this.now();
    this.state = createGameState(opts.runSeed, opts.classId, opts.playerName ?? 'Wanderer');
    this.log = new EventLog();
    this.rng = new RngBundle(opts.runSeed);
    this.activeDieId = this.content.dice[0]?.id ?? 'die.wooden';

    this.emit({ t: 0, type: 'RUN_STARTED', data: { seed: opts.runSeed } });
    this.emit({ t: 0, type: 'CLASS_CHOSEN', data: { classId: opts.classId } });

    for (const cardId of this.content.startingDeck) {
      this.state.deck.cards.push(cardId);
      this.state.deck.drawPile.push(cardId);
    }
    // Shuffle before the opening hand, or every run starts with the same cards.
    this.state.deck.drawPile = this.stream('deck').shuffle(this.state.deck.drawPile);
    this.drawCards(STARTING_HAND_SIZE);
    for (const itemId of this.content.startingItems[opts.classId] ?? []) {
      this.emit({ t: 0, type: 'ITEM_GAINED', data: { itemId } });
    }

    this.enterRoom(this.content.dungeon.def.entrance);
  }

  // -------------------------------------------------------------------------
  // Event plumbing
  // -------------------------------------------------------------------------

  /** The only way state changes. Appends to the log, then reduces. */
  emit(draft: EventDraft): GameEvent {
    const event = this.log.append(draft);
    applyEvent(this.state, event);
    return event;
  }

  private say(ev: SessionEvent): void {
    this.outbox.push(ev);
  }

  /** Take and clear pending presentation events. */
  drain(): SessionEvent[] {
    const out = this.outbox;
    this.outbox = [];
    return out;
  }

  private tick(): number {
    return ++this.state.world.tick;
  }

  private stream(name: RngStream) {
    return this.rng.get(name);
  }

  get currentRoom(): RoomDef | null {
    return this.state.currentRoom ? this.content.dungeon.room(this.state.currentRoom) : null;
  }

  get currentDie(): DieDefinition {
    return (
      this.content.dice.find((d) => d.id === this.activeDieId) ??
      this.content.dice[0] ?? {
        id: 'die.wooden',
        skin: 'wooden',
        material: 'wood',
        label: 'Wooden Die',
        glow: 'none',
      }
    );
  }

  // -------------------------------------------------------------------------
  // 1. ROLL
  // -------------------------------------------------------------------------

  /**
   * Roll the active die. Does not move anyone — the result becomes `pendingRoll`
   * so the player can manipulate it or choose how to spend it.
   */
  roll(context = 'movement'): RollRecord {
    // `dialogue` is included because dialogue encounters carry roll-gated
    // choices too (persuasion, insight); only a finished run forbids rolling.
    if (!ROLLABLE_PHASES.has(this.phase)) {
      throw new Error(`Cannot roll during phase '${this.phase}'`);
    }
    const die = this.currentDie;
    const record = rollDie(this.state.dice, this.stream('dice'), {
      die,
      context,
      tick: this.tick(),
      presentationSeed: presentationSeed(this.state.runSeed, this.state.dice.rollSeq + 1),
    });
    this.pendingRoll = record;
    this.emit({
      t: this.state.world.tick,
      type: 'DICE_ROLLED',
      rollRef: record.seq,
      data: { face: record.naturalFace, context, dieId: die.id, material: die.material },
    });
    this.say({
      kind: 'rolled',
      data: { record },
      sfxId: `sfx.dice.roll.${die.material}`,
    });
    if (context === 'movement') {
      this.movementPoints = record.finalFace;
      this.phase = 'awaitMove';
    }
    return record;
  }

  /** Spend a banked die face instead of rolling. */
  useBankedDie(index: number, context = 'movement'): RollRecord {
    const face = spendBanked(this.state.dice, index);
    const record: RollRecord = {
      seq: ++this.state.dice.rollSeq,
      dieId: this.activeDieId,
      material: this.currentDie.material,
      naturalFace: face,
      finalFace: face,
      band: bandOf(face),
      manipulations: ['spend'],
      splitInto: [],
      fateDebtAfter: this.state.dice.fateDebt,
      presentationSeed: presentationSeed(this.state.runSeed, this.state.dice.rollSeq),
      context,
      tick: this.tick(),
    };
    this.pendingRoll = record;
    this.emit({
      t: this.state.world.tick,
      type: 'DICE_ROLLED',
      rollRef: record.seq,
      data: { face, context, banked: true },
    });
    this.say({ kind: 'rolled', data: { record }, sfxId: 'sfx.dice.bank.spend' });
    if (context === 'movement') {
      this.movementPoints = face;
      this.phase = 'awaitMove';
    }
    return record;
  }

  /** Apply a manipulation to the pending roll, charging Fate Debt. */
  manipulate(kind: ManipulationKind): ManipulationResult {
    const record = this.pendingRoll;
    if (!record) throw new Error('No pending roll to manipulate');

    const dice = this.state.dice;
    let result: ManipulationResult;
    switch (kind) {
      case 'reroll': {
        // Fate Chain grants free rerolls; spend one before charging debt.
        if (this.freeRerolls > 0) {
          this.freeRerolls -= 1;
          // 무료라도 굴린 건 굴린 것이다 — 주사위 기억에는 똑같이 남아야 한다.
          result = mFreeReroll(dice, this.stream('dice'), record);
        } else {
          result = mReroll(dice, this.stream('dice'), record);
        }
        break;
      }
      case 'hold':
        result = mHold(dice, record);
        break;
      case 'split':
        result = mSplit(dice, record);
        break;
      case 'double':
        result = mDouble(dice, record);
        break;
      case 'sacrifice':
        result = mSacrifice(dice, record);
        break;
      case 'gamble':
        result = mGamble(dice, this.stream('dice'), record);
        break;
      case 'bank':
        result = mBank(dice, record);
        break;
      case 'spend':
        throw new Error("Use useBankedDie() to spend a banked die, not manipulate('spend')");
    }

    this.pendingRoll = result.record;
    if (result.record.context === 'movement') {
      this.movementPoints = result.record.finalFace;
    }

    this.emit({
      t: this.state.world.tick,
      type: 'DICE_MANIPULATED',
      rollRef: result.record.seq,
      data: { kind, face: result.record.finalFace, fateDebt: dice.fateDebt },
    });
    this.say({
      kind: 'manipulated',
      data: { kind, record: result.record },
      sfxId: `sfx.dice.${kind}`,
    });

    for (const threshold of result.thresholdsCrossed) {
      this.fireFateEvent(threshold);
    }
    return result;
  }

  /** Finalise the pending roll: update streaks and fire streak content. */
  private settleRoll(record: RollRecord): void {
    const streak = updateStreaks(this.state.dice, record);

    if (streak.fateChainTriggered) {
      this.emit({
        t: this.state.world.tick,
        type: 'FATE_CHAIN_TRIGGERED',
        rollRef: record.seq,
        data: { length: streak.fateChainLength },
      });
      const content = streakContentFor(FATE_CHAIN_CONTENT, streak.fateChainLength, FATE_CHAIN_TRIGGER);
      this.applyStreakContent(content, record.seq);
      this.say({ kind: 'streak', text: content.body, data: { streak: 'fateChain', content }, sfxId: 'sfx.fate.chain' });
    }

    if (streak.badLuckTriggered) {
      this.emit({
        t: this.state.world.tick,
        type: 'BAD_LUCK_TRIGGERED',
        rollRef: record.seq,
        data: { length: streak.badLuckLength },
      });
      const content = streakContentFor(BAD_LUCK_CONTENT, streak.badLuckLength, BAD_LUCK_TRIGGER);
      this.applyStreakContent(content, record.seq);
      this.say({ kind: 'streak', text: content.body, data: { streak: 'badLuck', content }, sfxId: 'sfx.fate.badLuck' });
    }
  }

  private applyStreakContent(content: StreakContent, rollRef: number): void {
    const compiled = compileEffects(content.effects, {
      tick: this.state.world.tick,
      room: this.state.currentRoom,
      rollRef,
    });
    this.applyCompiled(compiled);
    // Grants are engine-level, so they are read out of the effect data here.
    for (const eff of content.effects) {
      if (eff.data?.['grant'] === 'freeReroll') this.freeRerolls += 1;
      if (eff.data?.['grant'] === 'diceMemoryAwakened') this.state.world.flags.add('diceMemoryAwakened');
    }
  }

  private fireFateEvent(threshold: number): void {
    this.emit({
      t: this.state.world.tick,
      type: 'FATE_DEBT_THRESHOLD',
      data: { threshold, debt: this.state.dice.fateDebt },
    });
    const index = Math.min(
      FATE_EVENT_CONTENT.length - 1,
      [25, 60, 110, 180].indexOf(threshold) >= 0 ? [25, 60, 110, 180].indexOf(threshold) : 0,
    );
    const content = FATE_EVENT_CONTENT[index]!;
    this.applyStreakContent(content, this.pendingRoll?.seq ?? 0);
    this.say({ kind: 'fateEvent', text: content.body, data: { threshold, content }, sfxId: 'sfx.fate.debtThreshold' });
  }

  // -------------------------------------------------------------------------
  // 2. MOVE
  // -------------------------------------------------------------------------

  /** Destinations the pending movement roll can reach. */
  moveOptions(): { room: RoomId; cost: number; path: RoomId[]; name: string; kind: string }[] {
    if (!this.state.currentRoom) return [];
    return this.content.dungeon
      .reachable(this.state.currentRoom, this.movementPoints, {
        flags: this.state.world.flags,
        discoveredSecrets: this.state.world.discoveredSecrets,
      })
      .map((r) => {
        const def = this.content.dungeon.room(r.room);
        return { ...r, name: def.name, kind: def.kind };
      });
  }

  moveTo(roomId: RoomId): void {
    if (this.phase !== 'awaitMove') throw new Error(`Cannot move during phase '${this.phase}'`);
    const options = this.moveOptions();
    const chosen = options.find((o) => o.room === roomId);
    if (!chosen) throw new Error(`Room '${roomId}' is not reachable with ${this.movementPoints} movement`);

    const record = this.pendingRoll;
    this.emit({
      t: this.tick(),
      type: 'MOVED',
      room: roomId,
      ...(record ? { rollRef: record.seq } : {}),
      data: { from: this.state.currentRoom, distance: chosen.cost },
    });
    this.say({ kind: 'moved', data: { to: roomId, path: chosen.path }, sfxId: 'sfx.move.step' });

    if (record) this.settleRoll(record);
    this.pendingRoll = null;
    this.movementPoints = 0;
    this.enterRoom(roomId);
  }

  /**
   * 굴린 눈으로 갈 수 있는 방이 하나도 없으면 그 눈을 흘려보내고 제자리에 선다.
   *
   * 이게 없으면 화면이 멈췄다: 이동을 고를 방도, 다시 굴릴 단추도 없고, 남은 길은 운명 빚을
   * 내는 조작뿐이었다. 대부분의 방은 가장 가까운 문이 2칸이라 1 이 나올 때마다(1/6) 그랬고,
   * 비밀 방은 유일한 출구가 5칸이라 1~4 가 모두 그랬다. 측정기는 세션 필드를 직접 고쳐서
   * 이 구멍을 우회하고 있었다.
   *
   * 갈 곳이 있을 때는 흘려보낼 수 없다 — 원하는 눈이 나올 때까지 공짜로 다시 굴리는 길이 된다.
   * 흘려보낸 굴림도 굴림이다: 연속·불운 기억에 그대로 남는다(settleRoll).
   */
  stayBlocked(): string | null {
    if (this.phase !== 'awaitMove' || !this.pendingRoll) return '흘려보낼 굴림이 없다';
    if (this.moveOptions().length > 0) return '아직 갈 수 있는 곳이 있다';
    return null;
  }

  stay(): void {
    const blocked = this.stayBlocked();
    if (blocked) throw new Error(`제자리에 설 수 없다: ${blocked}`);
    const record = this.pendingRoll!;
    this.emit({
      t: this.tick(),
      type: 'MOVE_FORFEITED',
      ...(this.state.currentRoom ? { room: this.state.currentRoom } : {}),
      rollRef: record.seq,
      data: { face: record.finalFace },
    });
    this.settleRoll(record);
    this.pendingRoll = null;
    this.movementPoints = 0;
    this.say({
      kind: 'stayed',
      text: `🎲 ${record.finalFace} — 이 눈으로는 어디에도 닿지 않는다. 제자리에서 숨을 고른다.`,
      data: { face: record.finalFace },
      sfxId: 'sfx.ui.click',
    });
    this.phase = 'awaitRoll';
  }

  private enterRoom(roomId: RoomId): void {
    const room = this.content.dungeon.room(roomId);
    this.emit({ t: this.state.world.tick, type: 'ROOM_ENTERED', room: roomId, data: { kind: room.kind } });

    this.checkPromises();
    this.checkEasterEggs();

    /*
     * 이미 해결한 방은 다시 열지 않는다.
     *
     * 던전의 통로는 전부 양방향이라, 이 확인이 없으면 방 하나를 오가는 것만으로
     * 인카운터가 계속 다시 열리고 경험치와 아이템이 무한히 나온다. 실측으로
     * 방 11개짜리 한 판에서 인카운터가 22번 열렸다 — 이 게임은 "세계가 플레이어를
     * 기억한다"가 전부인데, 정작 방이 아무것도 기억하지 못했다.
     *
     * 기억의 저장소는 `clearedRooms` 하나로 충분하다. 인카운터는 방 하나에만
     * 붙어 있어서, 방이 닫히면 그 인카운터도 닫힌다. 한 인카운터가 여러 방에
     * 걸리는 콘텐츠가 생기면 그때 `EncounterDef.once` 를 위한 별도 집합이 필요하다
     * (지금은 어떤 콘텐츠도 `once` 를 쓰지 않는다).
     */
    if (this.state.world.clearedRooms.has(roomId)) {
      this.phase = 'awaitRoll';
      return;
    }

    if (room.encounterId) {
      const def = this.content.encounters.get(room.encounterId);
      if (def) {
        this.openEncounter(def);
        return;
      }
    }
    if (room.monsterIds?.length) {
      const monster = this.content.monsters.get(room.monsterIds[0]!);
      if (monster) {
        this.startCombat(monster);
        return;
      }
    }
    this.clearRoom(roomId);
  }

  private clearRoom(roomId: RoomId): void {
    if (!this.state.world.clearedRooms.has(roomId)) {
      this.emit({ t: this.state.world.tick, type: 'ROOM_CLEARED', room: roomId });
      // A card is a reward for resolving something, so an empty room — the
      // entrance most of all — must not pay one out for being walked into.
      const room = this.content.dungeon.room(roomId);
      const hadContent = Boolean(room.encounterId) || (room.monsterIds?.length ?? 0) > 0;
      if (hadContent) this.drawCards(1);
    }
    this.phase = 'awaitRoll';
  }

  // -------------------------------------------------------------------------
  // 2b. REST (GDD §12-1)
  // -------------------------------------------------------------------------

  /**
   * 지금 이 방에서 쉴 수 있는가. 쉴 수 없으면 까닭을 한 줄로 — 화면이 단추를 걸고 그대로 보여 준다.
   * `null` 은 쉴 수 있다는 뜻이다.
   */
  restBlocked(): string | null {
    if (this.state.ended) return '판이 끝났다';
    if (this.phase !== 'awaitRoll') return '지금은 쉴 때가 아니다';
    const room = this.currentRoom;
    if (!room?.rest) return '여기는 쉴 만한 곳이 아니다';
    if (!this.state.world.clearedRooms.has(room.id)) return '아직 이 방이 조용하지 않다';
    if (this.state.world.flags.has(restedFlag(room.id))) return '여기서는 이미 한 번 잤다';
    if (this.state.player.hp >= this.state.player.maxHp) return '쉴 필요가 없다';
    return null;
  }

  /** 이 판에서 아직 쓰지 않은 쉴 곳. 화면의 지도 표시와 측정기가 같은 목록을 본다. */
  restSitesLeft(): RoomDef[] {
    return this.content.dungeon.rooms.filter(
      (r) => r.rest && !this.state.world.flags.has(restedFlag(r.id)),
    );
  }

  /**
   * 쉰다. 휴식도 주사위다 — 굴린 눈이 회복을 정한다(1 악몽 · 2~5 얕은 잠 · 6 깊은 잠).
   * 상태는 `RESTED` 이벤트 하나로만 바뀐다(절대 규칙 3). 악몽의 운명 빚은 조작과 같은
   * `addFateDebt` 경로를 탄다 — 직접 더하면 임계치가 불발된다.
   */
  rest(): { face: DiceFace; kind: 'nightmare' | 'shallow' | 'deep'; healed: number; fateDebt: number } {
    const blocked = this.restBlocked();
    if (blocked) throw new Error(`쉴 수 없다: ${blocked}`);
    const room = this.currentRoom!;
    const record = this.roll('rest');
    const face = record.finalFace;
    const out = restOutcome(face, this.state.player.hp, this.state.player.maxHp);

    this.emit({
      t: this.tick(),
      type: 'RESTED',
      room: room.id,
      rollRef: record.seq,
      data: { face, kind: out.kind, healed: out.healed, fateDebt: out.fateDebt },
    });
    if (out.fateDebt > 0) {
      for (const threshold of addFateDebt(this.state.dice, out.fateDebt)) this.fireFateEvent(threshold);
    }
    this.settleRoll(record);
    this.pendingRoll = null;

    const text =
      out.kind === 'nightmare'
        ? room.rest!.nightmare ?? room.rest!.text
        : out.kind === 'deep'
          ? room.rest!.deep ?? room.rest!.text
          : room.rest!.text;
    this.say({
      kind: 'rested',
      text,
      data: { face, ...out, room: room.id },
      sfxId: out.kind === 'nightmare' ? 'sfx.fate.badLuck' : 'sfx.reward.common',
    });
    this.phase = 'awaitRoll';
    return { face, ...out };
  }

  // -------------------------------------------------------------------------
  // 3. ENCOUNTER + 4. CHOICE
  // -------------------------------------------------------------------------

  private openEncounter(def: EncounterDef): void {
    this.encounter = createEncounterRuntime(def);
    this.activeNpcId = def.npcId ?? null;
    this.phase = def.kind === 'dialogue' ? 'dialogue' : 'encounter';

    this.emit({ t: this.state.world.tick, type: 'ENCOUNTER_STARTED', data: { encounterId: def.id } });
    if (def.onEnter) {
      this.applyCompiled(
        compileEffects(def.onEnter, { tick: this.state.world.tick, room: this.state.currentRoom }),
      );
    }
    if (def.npcId) {
      const npc = npcMemory(this.state, def.npcId);
      this.seedNpc(def.npcId, npc);
      const firstMeeting = npc.metCount === 0;
      this.emit({
        t: this.state.world.tick,
        type: firstMeeting ? 'NPC_MET' : 'NPC_SPOKE',
        target: def.npcId,
        data: { summary: `${this.npcName(def.npcId)}와 만났다` },
      });
    }
    this.say({
      kind: 'encounterOpened',
      text: def.body,
      // `data` 를 두 번 쓰면 뒤엣것이 앞엣것을 통째로 덮는다. musicId 가 있는
      // 인카운터 6종에서 대사(dialogue)가 그렇게 사라지고 있었다.
      data: {
        encounter: def,
        dialogue: def.npcId ? this.dialogueFor(def.npcId) : null,
        ...(def.musicId ? { musicId: def.musicId } : {}),
      },
    });
  }

  /** Choices to render, with blocked ones surfaced rather than hidden. */
  choices(): ChoiceAvailability[] {
    if (!this.encounter) return [];
    return visibleChoices(this.encounter, this.state, (expr) => this.evalWorldCondition(expr));
  }

  /** GDD §17: decline to choose. May reveal a hidden option. */
  hesitate(): SelectedFragment[] | never[] {
    if (!this.encounter) throw new Error('No active encounter');
    const revealed = encounterWait(this.encounter);
    this.emit({
      t: this.tick(),
      type: 'CHOICE_TIMED_OUT',
      data: { encounterId: this.encounter.def.id, waits: this.encounter.waits },
    });
    if (revealed.length > 0) {
      this.say({
        kind: 'encounterOpened',
        text: '기다린 자에게만 보이는 것이 있다.',
        data: { revealed },
        sfxId: 'sfx.discovery.minor',
      });
    }
    return [];
  }

  /**
   * Resolve a choice. If the choice requires a roll, `face` must be supplied
   * (normally from `roll('choice')` then any manipulations).
   */
  choose(choiceId: string, face: DiceFace | null = null): void {
    const runtime = this.encounter;
    if (!runtime) throw new Error('No active encounter');
    const entry = this.choices().find((c) => c.choice.id === choiceId);
    if (!entry) throw new Error(`Unknown choice '${choiceId}'`);
    if (!entry.available) throw new Error(`Choice '${choiceId}' is not available (${entry.blockedBy})`);

    const choice = entry.choice;
    let effectiveFace = face;
    if (choice.roll && effectiveFace === null) {
      if (this.pendingRoll) effectiveFace = this.pendingRoll.finalFace;
      else throw new Error(`Choice '${choiceId}' requires a roll`);
    }

    const outcome = outcomeFor(choice, effectiveFace);
    if (!outcome) throw new Error(`Choice '${choiceId}' produced no outcome`);

    const rollRef = this.pendingRoll?.seq;
    runtime.resolvedChoiceId = choiceId;

    if (choice.requiresItem && !choice.keepItem) {
      this.emit({
        t: this.state.world.tick,
        type: 'ITEM_LOST',
        data: { itemId: choice.requiresItem },
      });
    }

    this.emit({
      t: this.tick(),
      type: 'CHOICE_MADE',
      ...(this.activeNpcId ? { target: this.activeNpcId } : {}),
      ...(rollRef !== undefined ? { rollRef } : {}),
      data: {
        encounterId: runtime.def.id,
        choiceId,
        ...(effectiveFace !== null ? { face: effectiveFace } : {}),
        band: effectiveFace !== null ? bandOf(effectiveFace) : 'none',
      },
    });

    const compiled = compileEffects(
      outcome.effects,
      { tick: this.state.world.tick, room: this.state.currentRoom, ...(rollRef !== undefined ? { rollRef } : {}) },
      outcome.goto,
    );
    this.applyCompiled(compiled);

    this.say({
      kind: 'choiceResolved',
      text: outcome.text,
      data: { choiceId, face: effectiveFace, band: effectiveFace !== null ? bandOf(effectiveFace) : null },
      ...(choice.sfxId ? { sfxId: choice.sfxId } : {}),
    });

    if (this.pendingRoll) {
      this.settleRoll(this.pendingRoll);
      this.pendingRoll = null;
    }

    this.afterConsequence();

    // A monster referenced by the encounter means the choice led to a fight.
    if (runtime.def.monsterId && !this.combat) {
      const monster = this.content.monsters.get(runtime.def.monsterId);
      if (monster && outcome.effects.some((e) => e.type === 'COMBAT_STARTED')) {
        this.encounter = null;
        this.startCombat(monster);
        return;
      }
    }

    if (compiled.goto && this.content.dungeon.has(compiled.goto)) {
      this.encounter = null;
      this.enterRoom(compiled.goto);
      return;
    }

    this.encounter = null;
    this.activeNpcId = null;
    this.dialogueCache.clear();
    if (this.state.currentRoom) this.clearRoom(this.state.currentRoom);
  }

  private applyCompiled(compiled: ReturnType<typeof compileEffects>): void {
    for (const draft of compiled.events) this.emit(draft);
    for (const flag of compiled.flags) this.state.world.flags.add(flag);

    if (compiled.hpDelta !== 0) {
      this.state.player.hp = Math.max(
        0,
        Math.min(this.state.player.maxHp, this.state.player.hp + compiled.hpDelta),
      );
    }
    if (compiled.goldDelta !== 0) {
      this.state.player.gold = Math.max(0, this.state.player.gold + compiled.goldDelta);
    }
    if (compiled.xpDelta !== 0) this.grantXp(compiled.xpDelta);
    if (compiled.fateDebtDelta !== 0) {
      if (compiled.fateDebtDelta < 0) {
        relieveFateDebt(this.state.dice, -compiled.fateDebtDelta);
      } else {
        // 주사위 조작과 똑같은 경로를 쓴다. 직접 더하면 상한을 넘고 임계치가
        // 불발되며, 건너뛴 임계치는 그 뒤로 영원히 터지지 않는다.
        for (const threshold of addFateDebt(this.state.dice, compiled.fateDebtDelta)) {
          this.fireFateEvent(threshold);
        }
      }
    }
    if (this.state.player.hp <= 0 && !this.state.ended) this.die();
  }

  // -------------------------------------------------------------------------
  // 4b. CARDS
  // -------------------------------------------------------------------------

  /** The player's hand, resolved to card definitions. */
  get hand(): CardDefRef[] {
    return this.state.deck.hand
      .map((id) => this.card(id))
      .filter((c): c is CardDefRef => c !== undefined);
  }

  /** Resolve a card id, tolerating transformed `base+overlay` ids. */
  card(id: string): CardDefRef | undefined {
    const exact = this.content.cards?.get(id);
    if (exact) return exact;
    const base = this.content.cards?.get(baseCardId(id));
    return base ? resolveVariant(id, base) : undefined;
  }

  /** Which context the player is currently in, for card legality. */
  get cardContext(): CardContext {
    if (this.pendingRoll) return 'roll';
    if (this.phase === 'combat') return 'combat';
    if (this.phase === 'encounter' || this.phase === 'dialogue') return 'encounter';
    if (this.phase === 'awaitMove') return 'move';
    return 'anytime';
  }

  /** Cards in hand that can legally be played right now. */
  playableCards(): CardDefRef[] {
    const ctx = this.cardContext;
    return this.hand.filter((c) => isPlayable(c, ctx));
  }

  /**
   * Draw from the draw pile, reshuffling the discard back in when it runs dry.
   * Returns the ids actually drawn — fewer than asked if the deck is exhausted.
   */
  drawCards(count = 1): string[] {
    const drawn: string[] = [];
    for (let i = 0; i < count; i++) {
      if (this.state.deck.hand.length >= MAX_HAND_SIZE) break;
      if (this.state.deck.drawPile.length === 0) {
        if (this.state.deck.discard.length === 0) break;
        this.state.deck.drawPile = this.stream('deck').shuffle(this.state.deck.discard);
        this.state.deck.discard = [];
        this.say({ kind: 'cardDrawn', text: '버린 카드를 다시 섞었다.', sfxId: 'sfx.card.shuffle' });
      }
      const id = this.state.deck.drawPile[0];
      if (id === undefined) break;
      this.emit({ t: this.state.world.tick, type: 'CARD_DRAWN', data: { cardId: id } });
      drawn.push(id);
    }
    if (drawn.length > 0) {
      this.say({
        kind: 'cardDrawn',
        data: { cards: drawn },
        sfxId: 'sfx.card.draw',
      });
    }
    return drawn;
  }

  /**
   * Play a card from hand. Throws if it is not in hand or not legal here, so a
   * UI bug surfaces instead of silently doing nothing.
   */
  playCard(cardId: string): void {
    const card = this.card(cardId);
    if (!card) throw new Error(`Unknown card '${cardId}'`);
    if (!this.state.deck.hand.includes(cardId)) throw new Error(`'${cardId}' is not in hand`);
    const ctx = this.cardContext;
    if (!isPlayable(card, ctx)) {
      throw new Error(`'${card.name}'은(는) 지금 낼 수 없다 (${ctx})`);
    }
    const play = card.play!;
    const rollRef = this.pendingRoll?.seq;

    this.emit({
      t: this.tick(),
      type: 'CARD_PLAYED',
      ...(rollRef !== undefined ? { rollRef } : {}),
      data: { cardId, name: card.name, context: ctx },
    });

    // --- roll manipulation -------------------------------------------------
    if (this.pendingRoll) {
      let face = this.pendingRoll.finalFace as number;
      if (play.setFace !== undefined) face = play.setFace;
      if (play.faceDelta !== undefined) face = face + play.faceDelta;
      face = Math.max(1, Math.min(6, face));
      if (face !== this.pendingRoll.finalFace) {
        this.pendingRoll = {
          ...this.pendingRoll,
          finalFace: face as DiceFace,
          band: bandOf(face as DiceFace),
        };
        if (this.pendingRoll.context === 'movement') this.movementPoints = face;
      }
      if (play.bankFace && this.state.dice.bank.length < this.state.dice.bankCapacity) {
        this.state.dice.bank.push(this.pendingRoll.finalFace);
      }
    }

    if (play.movementBonus) this.movementPoints += play.movementBonus;

    if (play.fateDebt) {
      if (play.fateDebt < 0) relieveFateDebt(this.state.dice, -play.fateDebt);
      else {
        const before = this.state.dice.fateDebt;
        this.state.dice.fateDebt = Math.min(240, before + play.fateDebt);
        for (const th of [25, 60, 110, 180]) {
          if (before < th && this.state.dice.fateDebt >= th && !this.state.dice.fateDebtFired.includes(th)) {
            this.state.dice.fateDebtFired.push(th);
            this.fireFateEvent(th);
          }
        }
      }
    }

    if (play.hp) {
      this.state.player.hp = Math.max(
        0,
        Math.min(this.state.player.maxHp, this.state.player.hp + play.hp),
      );
    }

    if (play.revealSecret) {
      this.emit({
        t: this.state.world.tick,
        type: 'SECRET_DISCOVERED',
        data: { secretId: play.revealSecret },
      });
    }

    if (play.effects?.length) {
      this.applyCompiled(
        compileEffects(play.effects, {
          tick: this.state.world.tick,
          room: this.state.currentRoom,
          ...(rollRef !== undefined ? { rollRef } : {}),
        }),
      );
    }

    // The CARD_PLAYED reducer already moved it to the discard; `remove` pulls it
    // out of the deck for good.
    if (play.remove) {
      this.emit({ t: this.state.world.tick, type: 'CARD_REMOVED', data: { cardId } });
    }

    this.say({
      kind: 'cardPlayed',
      text: `${card.name} — ${card.text}`,
      data: { cardId, name: card.name },
      sfxId: card.rarity === 'legendary' || card.rarity === 'mythic' ? 'sfx.card.rare' : 'sfx.card.flip',
    });

    if (this.state.player.hp <= 0 && !this.state.ended) this.die();
    this.afterConsequence();
  }

  // -------------------------------------------------------------------------
  // 5. COMBAT
  // -------------------------------------------------------------------------

  private startCombat(monster: MonsterDef): void {
    this.activeMonster = monster;
    this.combat = createCombat(monster, this.state.player.hp, this.state.player.maxHp);
    this.phase = 'combat';
    this.emit({
      t: this.state.world.tick,
      type: 'COMBAT_STARTED',
      target: monster.id,
      data: { tier: monster.tier },
    });
    this.say({
      kind: 'encounterOpened',
      text: `${monster.name}이(가) 길을 막는다.`,
      data: { monster },
      sfxId: monster.tier === 'boss' ? 'sfx.combat.bossAppear' : 'sfx.combat.appear',
    });
  }

  /** One combat round. `face` defaults to the pending roll. */
  fight(tactic: CombatTactic, face: DiceFace | null = null): void {
    const combat = this.combat;
    const monster = this.activeMonster;
    if (!combat || !monster) throw new Error('No active combat');

    const effectiveFace = face ?? this.pendingRoll?.finalFace;
    if (effectiveFace === undefined) throw new Error('Combat requires a roll');
    const rollRef = this.pendingRoll?.seq;

    const entry = stepCombat(
      combat,
      monster,
      tactic,
      effectiveFace,
      this.state.player.stats,
      this.stream('dice'),
      this.state.player.classId,
    );
    this.state.player.hp = combat.player.hp;
    this.tick();

    this.say({
      kind: 'combatRound',
      text: entry.text,
      data: { entry, monsterHp: combat.monster.hp, playerHp: combat.player.hp, phase: combat.phase },
      sfxId:
        entry.damageDealt > 0
          ? bandOf(effectiveFace) === 'critical'
            ? 'sfx.combat.crit'
            : 'sfx.combat.hit'
          : 'sfx.combat.miss',
    });

    if (this.pendingRoll) {
      this.settleRoll(this.pendingRoll);
      this.pendingRoll = null;
    }

    if (combat.outcome) this.endCombat(combat, monster, rollRef);
  }

  private endCombat(combat: CombatState, monster: MonsterDef, rollRef?: number): void {
    const isBoss = monster.tier === 'boss';
    const ref = rollRef !== undefined ? { rollRef } : {};

    switch (combat.outcome?.kind) {
      case 'won': {
        this.emit({ t: this.state.world.tick, type: 'COMBAT_WON', target: monster.id, ...ref });
        if (combat.outcome.killed) {
          this.emit({
            t: this.state.world.tick,
            type: isBoss ? 'BOSS_KILLED' : 'MONSTER_KILLED',
            target: monster.id,
            ...ref,
            data: { summary: `${monster.name}을(를) 쓰러뜨렸다` },
          });
          if (rollRef !== undefined) {
            rememberWithDie(this.state.dice, {
              dieId: this.activeDieId,
              eventType: isBoss ? 'BOSS_KILLED' : 'MONSTER_KILLED',
              summary: `이 주사위로 ${monster.name}을(를) 쓰러뜨렸다`,
              tick: this.state.world.tick,
              rollSeq: rollRef,
            });
          }
        } else {
          this.emit({
            t: this.state.world.tick,
            type: isBoss ? 'BOSS_SPARED' : 'MONSTER_SPARED',
            target: monster.id,
            ...ref,
          });
        }
        this.grantXp(monster.xp);
        const loot = rollLoot(monster, this.stream('loot'));
        if (loot) this.emit({ t: this.state.world.tick, type: 'ITEM_GAINED', data: { itemId: loot } });
        break;
      }
      case 'spared':
        this.emit({
          t: this.state.world.tick,
          type: isBoss ? 'BOSS_SPARED' : 'MONSTER_SPARED',
          target: monster.id,
          ...ref,
          data: { summary: `${monster.name}을(를) 살려주었다` },
        });
        this.grantXp(this.mercyXp(monster.xp, 1.2)); // mercy pays better than killing
        break;
      case 'recruited':
        this.emit({ t: this.state.world.tick, type: 'BOSS_RECRUITED', target: monster.id, ...ref });
        this.grantXp(this.mercyXp(monster.xp, 2));
        break;
      case 'bargained':
        this.emit({
          t: this.state.world.tick,
          type: 'COMBAT_FLED',
          target: monster.id,
          ...ref,
          data: { bargained: true, gave: combat.outcome.gave },
        });
        break;
      case 'fled':
        this.emit({ t: this.state.world.tick, type: 'COMBAT_FLED', target: monster.id, ...ref });
        break;
      case 'died':
        this.die();
        break;
    }

    this.say({
      kind: 'combatEnded',
      data: { outcome: combat.outcome, monsterId: monster.id },
      sfxId: combat.outcome?.kind === 'died' ? 'sfx.player.death' : 'sfx.combat.victory',
    });

    this.combat = null;
    this.activeMonster = null;
    this.afterConsequence();

    if (!this.state.ended) {
      const kind = combat.outcome?.kind;
      // 보스를 쓰러뜨렸거나, 살려 보냈거나, 데려갔으면 런이 끝난다.
      if (isBoss && (kind === 'won' || kind === 'spared' || kind === 'recruited')) {
        this.endRun('victory');
      } else if (this.state.currentRoom && kind !== 'fled') {
        /*
         * 도망친 방은 해결된 방이 아니다. 상대는 아직 거기 서 있다.
         *
         * 전에는 도망쳐도 방이 'cleared' 로 찍혔다. 그래도 티가 안 났던 이유는
         * 방을 다시 밟으면 인카운터가 어차피 또 열렸기 때문이다 — 버그 둘이
         * 서로를 가려 주고 있었다. 재진입 파밍을 막자 이쪽이 드러났다:
         * 보스에게서 한 번 도망치면 보스방이 영원히 닫혀, 런을 **이길 수도
         * 질 수도 없는** 상태가 된다. 실측으로 런의 35% 가 그렇게 끝나지 않았다.
         *
         * 거래(bargained)는 해결로 친다 — 대가를 치르고 길을 얻은 것이다.
         * 도망은 아니다 (GDD §7: "도망은 실패가 아니라 적극적인 전략" — 전략이
         * 되려면 돌아올 수 있어야 한다).
         */
        this.clearRoom(this.state.currentRoom);
      } else if (kind === 'fled') {
        this.phase = 'awaitRoll';
      }
    }
  }

  // -------------------------------------------------------------------------
  // 6. MEMORY + CONSEQUENCE
  // -------------------------------------------------------------------------

  /** Runs after any consequence: titles, hidden classes, easter eggs, promises. */
  private afterConsequence(): void {
    for (const title of checkTitles(this.state, this.log)) {
      this.emit({ t: this.state.world.tick, type: 'TITLE_EARNED', data: { title } });
      this.say({ kind: 'titleEarned', text: `'${title}'로 불리기 시작했다.`, data: { title }, sfxId: 'sfx.reward.legendary' });
    }
    for (const rule of checkHiddenClasses(this.state)) {
      this.say({
        kind: 'hiddenClass',
        text: `${rule.label}의 길이 열렸다.`,
        data: { rule },
        sfxId: 'sfx.reward.mythic',
      });
    }
    this.checkEasterEggs();
  }

  private checkPromises(): void {
    for (const npc of Object.values(this.state.npcs)) {
      const broken = duePromises(npc, this.state.world.tick, this.now());
      for (const p of broken) {
        this.emit({
          t: this.state.world.tick,
          type: 'PROMISE_BROKEN',
          target: npc.npcId,
          data: { promiseId: p.id, description: p.description, summary: `약속을 지키지 않았다: ${p.description}` },
        });
        this.say({
          kind: 'promiseBroken',
          text: `${this.npcName(npc.npcId)}와의 약속이 깨졌다.`,
          data: { promise: p, npcId: npc.npcId },
          sfxId: 'sfx.fate.promiseBroken',
        });
      }
    }
  }

  private checkEasterEggs(): void {
    for (const egg of this.content.easterEggs.values()) {
      if (this.state.world.foundEasterEggs.has(egg.id)) continue;
      if (!this.evalWorldCondition(egg.when)) continue;
      this.emit({ t: this.state.world.tick, type: 'EASTER_EGG_FOUND', data: { eggId: egg.id, name: egg.name } });
      this.say({ kind: 'easterEgg', text: egg.reveal, data: { egg }, sfxId: 'sfx.discovery.major' });
    }
  }

  /**
   * Evaluate a condition against world state rather than one NPC. Reuses the npc
   * grammar with a synthetic NPC so content authors learn one syntax, not two.
   */
  evalWorldCondition(expr: string | undefined): boolean {
    if (!expr) return true;
    const npc: NpcMemory = this.activeNpcId
      ? npcMemory(this.state, this.activeNpcId)
      : {
          npcId: '__world__',
          trust: 0,
          liking: 0,
          respect: 0,
          resentment: 0,
          debt: 0,
          impression: { kindness: 0, reliability: 0, menace: 0, foldedCount: 0 },
          memories: [],
          promises: [],
          knownFacts: [...this.state.world.knownFacts],
          concealedFacts: [],
          lastSeenTick: -1,
          metCount: 0,
          alive: true,
        };
    const ctx: DialogueContext = {
      npc,
      log: this.log,
      tick: this.state.world.tick,
      flags: this.worldFlagView(),
      usedFragments: this.usedFragments,
      playerFacts: this.state.world.knownFacts,
    };
    return evaluateCondition(expr, ctx);
  }

  /**
   * Flags plus derived pseudo-flags, so content can test things like
   * `flag:rolled.6` or `flag:trait.cruel` without the reducer storing them.
   */
  private worldFlagView(): ReadonlySet<string> {
    const view = new Set(this.state.world.flags);
    // Ids already carry their domain prefix, so they are added verbatim:
    // `flag:fact.theShattering`, `flag:secret.theTable`.
    for (const fact of this.state.world.knownFacts) view.add(fact);
    for (const secret of this.state.world.discoveredSecrets) view.add(secret);
    for (const [trait, value] of Object.entries(this.state.player.traits)) {
      if ((value ?? 0) > 0) view.add(`trait.${trait}`);
    }
    for (let f = 1; f <= 6; f++) {
      if ((this.state.world.aggregates[`rolls.face.${f}`] ?? 0) > 0) view.add(`rolled.${f}`);
    }
    if (this.state.dice.fateDebt >= 25) view.add('fateDebt.high');
    if (this.state.dice.badLuckStreak >= 3) view.add('badLuck.deep');
    if (this.state.dice.fateChain >= 3) view.add('fateChain.deep');
    for (const title of this.state.player.titles) view.add(`title.${title}`);
    return view;
  }

  /** Assemble this NPC's current dialogue from conditional fragments. */
  /**
   * 이 인카운터에서 이 NPC 가 할 말.
   *
   * 선택 결과를 인카운터 단위로 캐시한다. 이 함수는 `once` 대사를 소비하는데,
   * 클라이언트가 **렌더 중에** 호출한다 — 캐시가 없으면 React 가 두 번 렌더할
   * 때 첫 렌더에서 소비되고 두 번째 렌더에는 아무것도 안 남는다. 실제로
   * 1회성 대사 6종이 그렇게 한 번도 화면에 뜨지 못했다.
   *
   * 렌더는 순수해야 한다. 소비는 인카운터가 열릴 때 한 번만 일어나고,
   * 그 뒤의 호출은 같은 결과를 읽기만 한다.
   */
  dialogueFor(npcId: NpcId): SelectedFragment[] {
    const cacheKey = `${this.encounter?.def.id ?? '-'}:${npcId}`;
    const cached = this.dialogueCache.get(cacheKey);
    if (cached) return cached;

    const fragments = this.content.dialogue.get(npcId) ?? [];
    const npc = npcMemory(this.state, npcId);
    this.seedNpc(npcId, npc);
    const ctx: DialogueContext = {
      npc,
      log: this.log,
      tick: this.state.world.tick,
      flags: this.worldFlagView(),
      usedFragments: this.usedFragments,
      playerFacts: this.state.world.knownFacts,
    };
    const selected = selectFragments(fragments, ctx);
    for (const sel of selected) {
      const frag = fragments[sel.index];
      if (frag?.once) this.usedFragments.add(fragmentKey(frag, sel.index));
    }
    this.dialogueCache.set(cacheKey, selected);
    return selected;
  }

  /** Copy authored knowledge onto a fresh NpcMemory exactly once. */
  private seedNpc(npcId: NpcId, npc: NpcMemory): void {
    const seed = this.content.npcSeeds?.get(npcId);
    if (!seed) return;
    for (const fact of seed.knownFacts) {
      if (!npc.knownFacts.includes(fact)) npc.knownFacts.push(fact);
    }
    for (const fact of seed.concealedFacts) {
      if (!npc.concealedFacts.includes(fact)) npc.concealedFacts.push(fact);
    }
  }

  npcName(npcId: NpcId): string {
    return this.content.npcNames.get(npcId) ?? npcId;
  }

  npcRelationship(npcId: NpcId): { stage: string; npc: NpcMemory } {
    const npc = npcMemory(this.state, npcId);
    return { stage: relationshipStage(npc), npc };
  }

  // -------------------------------------------------------------------------
  // Progression and run end
  // -------------------------------------------------------------------------

  /**
   * 자비로 끝낸 전투의 경험치.
   *
   * 자비가 살육보다 이득인 것은 게임 전체의 주제라 모든 클래스에 적용된다.
   * 사제의 클래스 능력 (classes.json: "자비로 끝낸 전투에서 경험이 20% 더 쌓인다")
   * 은 그 위에 한 겹 더 얹는다. 전에는 1.2배가 전 클래스 공통이어서 사제의
   * 고유 능력이 아무 일도 하지 않았다.
   */
  private mercyXp(base: number, multiplier: number): number {
    const priestBonus = this.state.player.classId === 'priest' ? 1.2 : 1;
    return Math.floor(base * multiplier * priestBonus);
  }

  private grantXp(amount: number): void {
    if (amount <= 0) return;
    this.state.player.xp += amount;
    while (true) {
      // Recomputed each iteration — a single grant can cross two levels, and a
      // fixed `needed` would charge the second one the first level's price.
      const needed = xpForNextLevel(this.state.player.level);
      if (this.state.player.xp < needed) break;
      this.state.player.xp -= needed;
      this.state.player.level += 1;
      this.state.player.maxHp += HP_PER_LEVEL;
      this.state.player.hp = this.state.player.maxHp;
      // 능력치도 자란다. 체력만 주면 레벨업이 전투를 길게 만들 뿐이라,
      // 더 오래 맞는 것이 보상이 되어 버린다 (state.ts STAT_GROWTH 참조).
      this.state.player.stats = statsAtLevel(this.state.player.classId, this.state.player.level);
    }
  }

  private die(): void {
    this.emit({ t: this.state.world.tick, type: 'PLAYER_DIED', room: this.state.currentRoom ?? undefined });
    this.endRun('death');
  }

  endRun(reason: 'victory' | 'death' | 'abandoned'): void {
    if (this.state.ended) return;
    this.emit({ t: this.state.world.tick, type: 'RUN_ENDED', data: { reason } });
    for (const chapter of this.chronicle()) {
      this.emit({
        t: this.state.world.tick,
        type: 'CHRONICLE_CHAPTER',
        data: { chapter: chapter.chapter, title: chapter.title, body: chapter.lines.join('\n') },
      });
    }
    this.phase = 'runOver';
    this.say({ kind: 'runEnded', data: { reason, summary: this.summary() }, sfxId: reason === 'victory' ? 'sfx.reward.legendary' : 'sfx.player.death' });
  }

  chronicle(): ChronicleChapter[] {
    return buildChronicle(this.log, (id) => this.npcName(id));
  }

  summary(): RunSummary {
    return summarizeRun(this.state, this.log);
  }

  // -------------------------------------------------------------------------
  // Save / load
  // -------------------------------------------------------------------------

  save(): SaveFile {
    return serializeSave(
      this.state,
      this.log,
      {
        runSeed: this.state.runSeed,
        classId: this.state.player.classId,
        playerName: this.state.player.name,
        createdAtMs: this.createdAtMs,
        updatedAtMs: this.now(),
        playtimeMs: this.now() - this.createdAtMs,
      },
      this.rng.serialize(),
    );
  }

  /** Restore a save into a fresh session, preserving RNG cursors. */
  static load(
    raw: unknown,
    content: ContentBundle,
    opts?: { resolver?: ContentResolver; analytics?: AnalyticsSink; now?: () => number },
  ): { session: GameSession; unresolved: readonly string[]; migrationsApplied: readonly string[] } {
    const loaded = deserializeSave(raw, opts?.resolver);
    const session = new GameSession({
      runSeed: loaded.meta.runSeed,
      classId: loaded.meta.classId,
      playerName: loaded.meta.playerName,
      content,
      ...(opts?.analytics ? { analytics: opts.analytics } : {}),
      ...(opts?.now ? { now: opts.now } : {}),
    });

    // The constructor ran a fresh start; overwrite with the loaded state.
    Object.assign(session.state, loaded.state);
    (session as { log: EventLog }).log = loaded.log;
    session.rng.restore(loaded.rng);
    session.state.dice = loaded.state.dice ?? createDiceState();
    session.phase = session.state.ended ? 'runOver' : 'awaitRoll';
    session.pendingRoll = null;
    session.movementPoints = 0;
    session.encounter = null;
    session.combat = null;
    session.drain();

    return { session, unresolved: loaded.unresolved, migrationsApplied: loaded.migrationsApplied };
  }
}
